import { Box, Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import chaletApi from "api/chaletApi";
import { FormTextInput } from "components/common";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { Branch } from "types/Branch";
import * as yup from "yup";
import toast from "react-hot-toast";
import { toCurrencyFormat } from "utils/formatter";
import useUser from "hooks/useUser";

type BranchDepositFormType = {
  amount: string;
};
const validationSchema = yup.object({
  amount: yup.number().min(0).required("required"),
});
const useYupValidationResolver = (validationSchema: any) =>
  useCallback(
    async (data: any) => {
      try {
        const values = await validationSchema.validate(data, {
          abortEarly: false,
        });

        return {
          values,
          errors: {},
        };
      } catch (errors: any) {
        return {
          values: {},
          errors: errors.inner.reduce(
            (allErrors: any, currentError: any) => ({
              ...allErrors,
              [currentError.path]: {
                type: currentError.type ?? "validation",
                message: "Ingrese una cantidad válida",
              },
            }),
            {},
          ),
        };
      }
    },
    [validationSchema],
  );

export default function CreateBranchDeposit({ onComplete, branch }: { onComplete: () => void; branch: Branch }) {
  const { palette } = useTheme();
  const { user } = useUser();

  const [createDeposit, createDepositResult] = chaletApi.useCreateBranchDepositMutation();
  const { control, handleSubmit, setValue } = useForm<BranchDepositFormType>({
    defaultValues: {
      amount: "",
    },
    resolver: useYupValidationResolver(validationSchema),
  });

  const onCreateDeposit = async ({ amount }: BranchDepositFormType) => {
    try {
      const userId = user?.id;
      const sectorBalanceId = user?.sectorWork.sector.sectorBalanceId;
      const deposit = await createDeposit({
        amount: parseFloat(amount),
        id: branch.id,
        sectorBalanceId,
        userId,
      }).unwrap();
      setValue("amount", "");
      console.log("Deposit result", deposit);
      onComplete();
      toast.success(`Deposito realizado con éxito`);
    } catch (error: any) {
      console.log("Deposit error", error);

      let msg = "Ha ocurrido un error";
      if (error.data && error.data.error) {
        msg += `: ${error.data.error}`;
      }
      toast.error(msg);
    }
  };

  return (
    <form onSubmit={handleSubmit(onCreateDeposit)}>
      <Box sx={{ backgroundColor: palette.background.default, padding: 2 }}>
        <Stack flexDirection="column" alignItems="center" gap={2}>
          <Stack flexDirection="column">
            <Typography variant="h6">
              {branch.name} {toCurrencyFormat(branch.wallet?.currentBalance || 0)}
            </Typography>
          </Stack>
          <FormTextInput
            control={control}
            label="Cantidad"
            name="amount"
            variant="outlined"
            disabled={createDepositResult.isLoading}
          />
          {createDepositResult.isLoading && <CircularProgress />}
          <Button
            variant="contained"
            disabled={createDepositResult.isLoading}
            type="submit"
            sx={{
              mt: 4,
              alignSelf: "center",
              mb: 4,
            }}
          >
            Realizar deposito
          </Button>
        </Stack>
        {createDepositResult?.data && (
          <Box sx={{ backgroundColor: palette.success.contrastText, padding: 2 }}>
            <Typography variant="h6">Deposito realizado</Typography>
            <Typography variant="body1">Deposito realizado con éxito</Typography>
            {/* nuevo saldo */}
            <Typography variant="body1">Nuevo saldo: {createDepositResult?.data?.wallet?.currentBalance}</Typography>
          </Box>
        )}
      </Box>
    </form>
  );
}
